import Phaser from 'phaser';
import { coinLabel } from '../../config/coinLabel';
import {
  CHAPTERS,
  levelsInChapter,
  LAST_LEVEL_ID,
  hasBoss,
  expRewardFor,
} from '../../config/levels';
import { ARENA_LEVEL_ID } from '../../config/testArena';
import type { LevelDef } from '../../core/game';
import { LocalScoreService } from '../../services/scoreService';
import { ProgressService } from '../../services/progressService';
import { saveStorage } from '../saveStorage';
import { recordStat } from '../quests';
import { sweepAndToastAchievements } from '../../ui/achievementToast';
import { getAudioService } from '../audio/AudioService';
import { getMusicService } from '../audio/MusicService';
import { addMuteButton } from '../audio/audioControls';
import { addFullscreenButton } from '../screenControls';
import { installDebugReturn, isDebugEnabled } from '../debugMode';
import { spinCoin } from '../coin';
import { openTowerGuide } from '../towerGuide';
import { availableTowers, rosterThroughLevel } from '../../config/unlocks';
import { LEVELS } from '../../config/levels';
import { drawStarfield } from '../starfield';
import {
  buildTabRow,
  chapterAccent,
  LEADERBOARD_TAB,
  TAB_ROW_Y,
  type TabId,
  type TabRow,
} from '../chapterTabs';
import { leaderboardAvailable } from '../../account/leaderboardApi';

/** Star glyphs (0–3) — filled gold ★ then hollow ☆ to fill out of 3. */
function starString(stars: number): string {
  const n = Math.max(0, Math.min(3, stars));
  return '★'.repeat(n) + '☆'.repeat(3 - n);
}

/**
 * Row geometry. Rows are centred within fixed-height slots; ROW_H is the drawn
 * height of each panel and is independent of the slot spacing, so the row CENTRE
 * (and the e2e click target for Level 1 at 640,175) stays put if ROW_H changes.
 *
 * Every chapter uses the SAME slot height (Chapter 1's layout). A chapter with
 * more rows than fit in the column SCROLLS (arrows / wheel / swipe) instead of
 * squishing its panels — so Chapter 2's cards match Chapter 1's size, and its
 * top row centre sits at (640,175) just like Chapter 1's.
 */
const ROW_H = 92;
const ROW_W = 880;
/** Top / bottom of the level-row column. */
const AREA_TOP = 120;
const AREA_BOTTOM = 670;
/** Vertical spacing between row centres — Chapter 1's (670−120)/5. */
const SLOT_H = 110;
/** Rows visible at once; chapters with more scroll by whole rows. */
const VISIBLE_ROWS = Math.floor((AREA_BOTTOM - AREA_TOP) / SLOT_H); // = 5
/** Wheel-delta accumulation needed to step one row (trackpad smoothing). */
const WHEEL_STEP = 30;
/** Minimum vertical pointer travel for a swipe-scroll (also the tap cutoff). */
const SWIPE_MIN = 50;

const GOLD_HEX = '#f5c518';
/** Chapter 2 accent — ice blue; also the debug unlock button's fill. */
const CH2_BLUE_HEX = '#4aa3e0';

/** First (lowest-id) level in a chapter — the one that gates the chapter's tab. */
function firstLevelOf(chapter: number): LevelDef {
  return levelsInChapter(chapter)[0];
}

/**
 * LevelSelectScene
 * "HAMBI WARZ" campaign select: a starfield backdrop and one themed panel per
 * level (episode numeral, parody title, location, difficulty pips, best score).
 * Tapping a level starts GameScene with { levelId } and launches HudScene.
 * "Back" returns to MenuScene.
 */
export class LevelSelectScene extends Phaser.Scene {
  private scoreService!: LocalScoreService;
  private progress!: ProgressService;
  /** Currently shown chapter (default 1 — keeps Level 1 at (640,175) for e2e). */
  private selectedChapter = 1;
  /**
   * Chapter requested by the scene that started us (e.g. the level a player just
   * finished). Applied in create() once progress is known — only if that chapter
   * exists and is unlocked — so finishing a Chapter 2 episode and tapping
   * "Episodes" reopens on Chapter 2 rather than snapping back to Chapter 1.
   */
  private requestedChapter?: number;
  /** Container holding the current chapter's rows; destroyed on chapter switch. */
  private rowsContainer?: Phaser.GameObjects.Container;
  /** The shared four-tab row (CHAPTER 1-3 + LEADERBOARDS). */
  private tabRow?: TabRow;
  /** Transient "locked" hint shown when a locked chapter tab is tapped. */
  private lockHint?: Phaser.GameObjects.Text;
  /** Index of the first visible row when the chapter overflows the column. */
  private scrollOffset = 0;
  /** Accumulated wheel delta — steps one row per WHEEL_STEP. */
  private wheelAccum = 0;

  constructor() {
    super({ key: 'LevelSelectScene' });
  }

  /** Capture the optional chapter to open on before create() runs. */
  init(data: { chapter?: number }): void {
    this.requestedChapter = data?.chapter;
  }

  create(): void {
    const cx = 640;

    this.selectedChapter = 1;
    this.rowsContainer = undefined;
    this.tabRow = undefined;
    this.scrollOffset = 0;
    this.wheelAccum = 0;

    drawStarfield(this, 1280, 720);

    // ── Row scrolling: mouse wheel + vertical swipe over the row column ──────
    this.input.on('wheel', (_p: Phaser.Input.Pointer, _over: unknown, _dx: number, dy: number) => {
      this.wheelAccum += dy;
      if (Math.abs(this.wheelAccum) >= WHEEL_STEP) {
        this.scrollBy(this.wheelAccum > 0 ? 1 : -1);
        this.wheelAccum = 0;
      }
    });
    this.input.on('pointerup', (p: Phaser.Input.Pointer) => {
      const dy = p.y - p.downY;
      if (Math.abs(dy) >= SWIPE_MIN && p.downY > AREA_TOP && p.downY < AREA_BOTTOM) {
        this.scrollBy(dy < 0 ? 1 : -1); // dragging up reveals later episodes
      }
    });

    // ── Title ──────────────────────────────────────────────────────────────
    this.add
      .text(cx, 48, 'CHOOSE YOUR BATTLE', {
        fontSize: '38px',
        color: GOLD_HEX,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.add
      .text(cx, 84, 'THE HAMBI WARZ CAMPAIGN', { fontSize: '15px', color: '#9aa6ad' })
      .setOrigin(0.5);

    // ── Back button ──────────────────────────────────────────────────────────
    const backBtn = this.add
      .text(80, 50, '← BACK', {
        fontSize: '22px',
        color: '#cfe0bd',
        backgroundColor: '#15131f',
        padding: { x: 16, y: 12 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    backBtn.on('pointerover', () => backBtn.setColor(GOLD_HEX));
    backBtn.on('pointerout', () => backBtn.setColor('#cfe0bd'));
    backBtn.on('pointerup', () => this.scene.start('MenuScene'));

    // ── "?" tower guide (next to Back) — the towers unlocked so far ──────────
    const helpBtn = this.add
      .text(176, 50, '?', {
        fontSize: '22px',
        color: '#9fd8ff',
        fontStyle: 'bold',
        backgroundColor: '#15131f',
        padding: { x: 18, y: 12 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    helpBtn.on('pointerover', () => helpBtn.setColor(GOLD_HEX));
    helpBtn.on('pointerout', () => helpBtn.setColor('#9fd8ff'));
    helpBtn.on('pointerup', () => {
      // Include the extras — win-granted (the Wizard Tower) and challenge-earned
      // (Shrekbook Hambi) alongside shop-owned ones — otherwise they never
      // appear in the episode-picker guide.
      const roster = availableTowers(this.progress.enteredLevels(), this.progress.unlockedExtras());
      openTowerGuide(this, roster.length ? roster : rosterThroughLevel(LEVELS[0].id));
    });

    // ── Reset Settings button (right of the "?") ─────────────────────────────
    const resetBtn = this.add
      .text(330, 50, '⟳ Reset Settings', {
        fontSize: '18px',
        color: '#e8a0a0',
        backgroundColor: '#2a1414',
        padding: { x: 14, y: 12 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    resetBtn.on('pointerover', () => resetBtn.setColor('#ff6b6b'));
    resetBtn.on('pointerout', () => resetBtn.setColor('#e8a0a0'));
    resetBtn.on('pointerup', () => this.showResetSettings());

    // Mute toggle — far top-right; clear of Back (80,50) and the coin balance (960).
    addMuteButton(this, 1245, 50, getAudioService(this));
    addFullscreenButton(this, 1100, 50); // free gap between the coin balance and mute

    // ── Test Arena button ────────────────────────────────────────────────────
    // Top-right of the row BELOW the toolbar, sharing the chapter-tab line but
    // well clear of it (the tabs are centred and reach x≈1065) and of the scroll
    // arrows further down at (1140, ~349/441).
    const testBtn = this.add
      .text(1180, 108, 'TEST', {
        fontSize: '18px',
        color: '#ffffff',
        fontStyle: 'bold',
        backgroundColor: '#1a3a7a',
        padding: { x: 16, y: 12 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    testBtn.on('pointerover', () => testBtn.setBackgroundColor('#2a5aaa'));
    testBtn.on('pointerout', () => testBtn.setBackgroundColor('#1a3a7a'));
    testBtn.on('pointerup', () => {
      this.scene.start('GameScene', { levelId: ARENA_LEVEL_ID });
      this.scene.launch('HudScene', { levelId: ARENA_LEVEL_ID, arena: true });
    });

    // ── Storage button ───────────────────────────────────────────────────────
    // Directly under TEST, in grey: the shelf of unopened takeout bags from the
    // Restaurant. Sits above the scroll arrows (y≈349) with room to spare.
    const storageBtn = this.add
      .text(1180, 162, 'STORAGE', {
        fontSize: '15px',
        color: '#e8ecef',
        fontStyle: 'bold',
        backgroundColor: '#4a4f57',
        padding: { x: 10, y: 10 },
      })
      .setOrigin(0.5)
      .setName('levelselect-storage')
      .setInteractive({ useHandCursor: true });
    storageBtn.on('pointerover', () => storageBtn.setBackgroundColor('#666c76'));
    storageBtn.on('pointerout', () => storageBtn.setBackgroundColor('#4a4f57'));
    storageBtn.on('pointerup', () => this.scene.start('StorageScene'));

    // ── Achievements button ──────────────────────────────────────────────────
    // Directly under STORAGE, in the game's gold: ranks, exp and the badge
    // gallery. Still clear of the scroll arrows at y≈349.
    const achBtn = this.add
      .text(1180, 216, 'ACHIEVEMENTS', {
        fontSize: '15px',
        color: '#1a1a1a',
        fontStyle: 'bold',
        backgroundColor: '#f5c518',
        padding: { x: 10, y: 10 },
      })
      .setOrigin(0.5)
      .setName('levelselect-achievements')
      .setInteractive({ useHandCursor: true });
    achBtn.on('pointerover', () => achBtn.setBackgroundColor('#ffd83a'));
    achBtn.on('pointerout', () => achBtn.setBackgroundColor('#f5c518'));
    achBtn.on('pointerup', () => this.scene.start('AchievementsScene'));

    // Loop the episode-selection theme while this screen is up; stop it when the
    // scene shuts down (into a level, the shop, or the menu) so the next scene's
    // own track — or silence — takes over cleanly.
    const music = getMusicService(this);
    music.playTrack('episode-selection');
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => music.stop());

    // ── Progress / score services ────────────────────────────────────────────
    this.scoreService = new LocalScoreService(saveStorage());
    this.progress = new ProgressService(saveStorage(), LAST_LEVEL_ID);

    // ── Coin balance (top bar, free zone left of Reset) ──────────────────────
    const coinIcon = this.add.image(960, 50, 'coin').setDisplaySize(34, 34).setDepth(5);
    spinCoin(this, coinIcon);
    this.add
      .text(982, 50, coinLabel(this.progress.coins()), {
        fontSize: '22px',
        color: GOLD_HEX,
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(5);

    // Open on the requested chapter (e.g. the one a player just finished) when
    // it exists and is unlocked; otherwise stay on Chapter 1.
    if (
      this.requestedChapter != null &&
      CHAPTERS.includes(this.requestedChapter) &&
      this.isChapterUnlocked(this.requestedChapter)
    ) {
      this.selectedChapter = this.requestedChapter;
    }

    // ── Chapter tabs + initial chapter render ─────────────────────────────────
    this.buildTabs();
    if (isDebugEnabled()) {
      this.addDebugUnlockButton();
      this.addDebugPhantomButton();
    }
    this.renderChapter(this.selectedChapter);

    // State-based achievements: the episode screen is the natural checkpoint
    // after inbox deliveries (/give tower, /op) land or a session restores.
    sweepAndToastAchievements(saveStorage());

    installDebugReturn(this); // Esc → DemoScene when launched via ?debug
  }

  /** Is a chapter's tab playable (chapter 1 always; others once their opener unlocks)? */
  private isChapterUnlocked(chapter: number): boolean {
    return chapter <= CHAPTERS[0] || this.progress.isUnlocked(firstLevelOf(chapter).id);
  }

  /**
   * Build the tab row. The fourth tab only exists when there is a backend to
   * rank against — a build with no VITE_SUPABASE_* hides it entirely, the same
   * way it hides the account button.
   */
  private buildTabs(): void {
    this.tabRow?.destroy();
    this.tabRow = buildTabRow(this, {
      selected: this.selectedChapter,
      isChapterUnlocked: (c) => this.isChapterUnlocked(c),
      onSelect: (id) => this.onTab(id),
      onLocked: (c) => this.flashLockHint(c),
      showLeaderboards: leaderboardAvailable(),
    });
  }

  /** Tab click: switch chapter, or hand over to the leaderboard. */
  private onTab(id: TabId): void {
    if (id === LEADERBOARD_TAB) {
      this.scene.start('LeaderboardScene');
      return;
    }
    this.selectedChapter = id;
    this.scrollOffset = 0;
    this.tabRow?.restyle(id);
    this.renderChapter(id);
  }

  /** Step the row window by `step` rows (if the chapter overflows) and redraw. */
  private scrollBy(step: number): void {
    const maxOffset = Math.max(0, levelsInChapter(this.selectedChapter).length - VISIBLE_ROWS);
    const next = Phaser.Math.Clamp(this.scrollOffset + step, 0, maxOffset);
    if (next === this.scrollOffset) return;
    this.scrollOffset = next;
    this.renderChapter(this.selectedChapter);
  }

  /** Briefly show how to unlock a locked chapter, below the tab row. */
  private flashLockHint(chapter = 2): void {
    this.lockHint?.destroy();
    // Each chapter unlocks by beating the previous chapter's finale.
    const hint =
      chapter >= 3
        ? "Beat Chapter 2's finale to unlock Chapter 3"
        : 'Beat Episode V to unlock Chapter 2';
    this.lockHint = this.add
      .text(640, TAB_ROW_Y + 30, hint, {
        fontSize: '15px',
        color: chapterAccent(chapter).hex,
        fontStyle: 'italic',
      })
      .setOrigin(0.5)
      .setDepth(50);
    this.time.delayedCall(1800, () => {
      this.lockHint?.destroy();
      this.lockHint = undefined;
    });
  }

  /**
   * Draw the rows for one chapter into a fresh container (replacing any prior
   * one). Every chapter uses Chapter 1's fixed SLOT_H spacing and full-size
   * ROW_H panels, so Level 1 stays centred at (640,175). A chapter with more
   * than VISIBLE_ROWS levels shows a window of rows starting at scrollOffset,
   * stepped by the arrow buttons, mouse wheel, or a vertical swipe.
   */
  private renderChapter(chapter: number): void {
    this.rowsContainer?.destroy();
    const container = this.add.container(0, 0).setDepth(1);
    this.rowsContainer = container;

    const cx = 640;
    const levels = levelsInChapter(chapter);
    const maxOffset = Math.max(0, levels.length - VISIBLE_ROWS);
    this.scrollOffset = Phaser.Math.Clamp(this.scrollOffset, 0, maxOffset);
    const accent = chapterAccent(chapter);

    const visible = levels.slice(this.scrollOffset, this.scrollOffset + VISIBLE_ROWS);
    visible.forEach((level, i) => {
      const rowY = AREA_TOP + SLOT_H * i + SLOT_H / 2;
      this.makeLevelRow(cx, rowY, level, accent, container);
    });

    if (maxOffset > 0) this.buildScrollUi(container, levels.length, maxOffset, accent);
  }

  /**
   * ▲ / ▼ step buttons plus an "a–b / n" range readout in the right margin —
   * only built when the chapter overflows the column. Added to the rows
   * container so a chapter switch tears them down with the rows.
   */
  private buildScrollUi(
    container: Phaser.GameObjects.Container,
    totalRows: number,
    maxOffset: number,
    accent: { num: number; hex: string },
  ): void {
    const x = 1140;
    const midY = (AREA_TOP + AREA_BOTTOM) / 2;

    const makeArrow = (y: number, glyph: string, step: number, enabled: boolean) => {
      const btn = this.add
        .text(x, y, glyph, {
          fontSize: '22px',
          color: enabled ? accent.hex : '#555a66',
          backgroundColor: '#15131f',
          padding: { x: 14, y: 10 },
        })
        .setOrigin(0.5)
        .setDepth(2);
      container.add(btn);
      if (enabled) {
        btn.setInteractive({ useHandCursor: true });
        btn.on('pointerover', () => btn.setBackgroundColor('#262234'));
        btn.on('pointerout', () => btn.setBackgroundColor('#15131f'));
        btn.on('pointerup', (p: Phaser.Input.Pointer) => {
          if (p.getDistance() < SWIPE_MIN) this.scrollBy(step); // swipes step via the scene handler
        });
      }
    };

    makeArrow(midY - 46, '▲', -1, this.scrollOffset > 0);
    makeArrow(midY + 46, '▼', +1, this.scrollOffset < maxOffset);

    const first = this.scrollOffset + 1;
    const last = Math.min(this.scrollOffset + VISIBLE_ROWS, totalRows);
    container.add(
      this.add
        .text(x, midY, `${first}–${last} / ${totalRows}`, { fontSize: '13px', color: '#9aa6ad' })
        .setOrigin(0.5)
        .setDepth(2),
    );
  }

  /**
   * Debug-only (`?debug`): grant Phantom Hambi outright.
   *
   * He is the Normal Takeout Bag's 5% slot, so testing him any other way means
   * ordering bags for ten minutes at a time until the rare roll lands. Sits
   * beside UNLOCK CH2 and, like it, only exists under `?debug`.
   *
   * The grant lasts for this page load only: under `?debug` `this.progress` is
   * backed by the sandbox (`saveStorage()`), so Phantom is owned everywhere the
   * session goes — Equip, the Shop shelf, the episode — and is gone on reload,
   * leaving the player's real save without him.
   */
  private addDebugPhantomButton(): void {
    const btn = this.add
      .text(870, 692, '👻 PHANTOM UNLOCK (debug)', {
        fontSize: '14px',
        color: '#0c0b16',
        backgroundColor: '#9fd8ff',
        padding: { x: 12, y: 6 },
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(60)
      .setName('debug-phantom-unlock')
      .setInteractive({ useHandCursor: true });
    btn.on('pointerup', () => {
      const fresh = this.progress.awardTower('phantom');
      if (fresh) recordStat('obtains');
      btn.setText(fresh ? '👻 PHANTOM UNLOCKED' : '👻 ALREADY OWNED');
      btn.setBackgroundColor('#6ecb3c');
    });
  }

  /** Debug-only (`?debug`): unlock Chapter 2 and jump to its tab for testing. */
  private addDebugUnlockButton(): void {
    const btn = this.add
      .text(410, 692, '🔓 UNLOCK CH2 (debug)', {
        fontSize: '14px',
        color: '#0c0b16',
        backgroundColor: CH2_BLUE_HEX,
        padding: { x: 12, y: 6 },
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(60)
      .setInteractive({ useHandCursor: true });
    btn.on('pointerup', () => {
      this.progress.unlockTo(LAST_LEVEL_ID);
      const ch2 = CHAPTERS.find((c) => c > CHAPTERS[0]) ?? CHAPTERS[0];
      this.selectedChapter = ch2;
      this.scrollOffset = 0;
      this.buildTabs();
      this.renderChapter(ch2);
    });
  }

  /**
   * Modal "Reset Settings" panel. Each category — Coins, Episode Progress, Score,
   * Towers — is an independent toggle (all OFF to start); the "All" toggle is a
   * master that flips every category on/off together and reflects "on" only when
   * every category is selected. CONFIRM wipes exactly the selected categories from
   * storage and restarts the scene; CANCEL tears the overlay down, changing nothing.
   */
  private showResetSettings(): void {
    const W = 1280;
    const H = 720;
    const D = 100; // above every level-select element
    const objects: Phaser.GameObjects.GameObject[] = [];
    const panelCx = W / 2;
    const panelTop = 150;

    objects.push(this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.7).setDepth(D));
    objects.push(
      this.add
        .rectangle(panelCx, panelTop + 233, 560, 466, 0x1a1726, 1)
        .setStrokeStyle(2, 0xcc4444)
        .setDepth(D + 1),
    );
    objects.push(
      this.add
        .text(panelCx, panelTop + 40, 'Reset Settings', {
          fontSize: '30px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    objects.push(
      this.add
        .text(panelCx, panelTop + 78, 'Choose what to reset — this cannot be undone.', {
          fontSize: '15px',
          color: '#9aa6ad',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );

    // ── Toggle state ─────────────────────────────────────────────────────────
    type Key = 'coins' | 'progress' | 'score' | 'towers' | 'bags';
    const state: Record<Key, boolean> = {
      coins: false,
      progress: false,
      score: false,
      towers: false,
      bags: false,
    };
    const allOn = (): boolean =>
      state.coins && state.progress && state.score && state.towers && state.bags;
    const anyOn = (): boolean =>
      state.coins || state.progress || state.score || state.towers || state.bags;

    const rowDefs: { key: Key | 'all'; label: string; hint: string }[] = [
      { key: 'all', label: 'All', hint: 'Everything below' },
      { key: 'coins', label: 'Coins', hint: 'Reset coin balance to 0' },
      { key: 'progress', label: 'Episode Progress', hint: 'Stars, unlocks & tutorials' },
      { key: 'score', label: 'Score', hint: 'Clear best scores to 0' },
      { key: 'towers', label: 'Towers', hint: 'Starting towers & evolutions' },
      { key: 'bags', label: 'Bags', hint: 'Remove all stored bags' },
    ];

    const rows: { key: Key | 'all'; box: Phaser.GameObjects.Rectangle }[] = [];
    const btnY = panelTop + 418;
    const confirmBg = this.add
      .rectangle(panelCx - 120, btnY, 200, 52, 0x342230)
      .setStrokeStyle(2, 0xcc4444)
      .setInteractive({ useHandCursor: true })
      .setDepth(D + 2);
    objects.push(confirmBg);
    const confirmTxt = this.add
      .text(panelCx - 120, btnY, 'CONFIRM', {
        fontSize: '20px',
        fontStyle: 'bold',
        color: '#7d7088',
      })
      .setOrigin(0.5)
      .setDepth(D + 3);
    objects.push(confirmTxt);

    const refresh = (): void => {
      for (const r of rows) {
        const on = r.key === 'all' ? allOn() : state[r.key];
        r.box.setFillStyle(on ? 0xcc4444 : 0x2a2636);
        r.box.setStrokeStyle(2, on ? 0xff8a8a : 0x5a5470);
      }
      confirmBg.setFillStyle(anyOn() ? 0x7a1a1a : 0x342230);
      confirmTxt.setColor(anyOn() ? '#ffffff' : '#7d7088');
    };

    const toggle = (key: Key | 'all'): void => {
      if (key === 'all') {
        const target = !allOn();
        state.coins = state.progress = state.score = state.towers = state.bags = target;
      } else {
        state[key] = !state[key];
      }
      refresh();
    };

    // ── Toggle rows ──────────────────────────────────────────────────────────
    const rowX = panelCx - 210; // left edge of the checkbox column
    rowDefs.forEach((def, i) => {
      const y = panelTop + 120 + i * 46;
      const box = this.add
        .rectangle(rowX, y, 26, 26, 0x2a2636)
        .setStrokeStyle(2, 0x5a5470)
        .setDepth(D + 2);
      objects.push(box);
      objects.push(
        this.add
          .text(rowX + 26, y, def.label, {
            fontSize: '20px',
            color: '#f0e6d2',
            fontStyle: def.key === 'all' ? 'bold' : 'normal',
          })
          .setOrigin(0, 0.5)
          .setDepth(D + 2),
      );
      objects.push(
        this.add
          .text(rowX + 250, y, def.hint, { fontSize: '13px', color: '#7f8896' })
          .setOrigin(0, 0.5)
          .setDepth(D + 2),
      );
      // Wide invisible hit area spanning the whole row (checkbox + labels).
      const hit = this.add
        .rectangle(panelCx, y, 460, 38, 0x000000, 0.001)
        .setInteractive({ useHandCursor: true })
        .setDepth(D + 3);
      hit.on('pointerup', () => toggle(def.key));
      objects.push(hit);
      rows.push({ key: def.key, box });
    });

    const teardown = (): void => objects.forEach((o) => o.destroy());

    // ── Confirm (applies selected resets) ────────────────────────────────────
    confirmBg.on('pointerup', () => {
      if (!anyOn()) return; // nothing selected → no-op
      try {
        const progress = new ProgressService(saveStorage(), LAST_LEVEL_ID);
        if (state.coins) progress.resetCoins();
        if (state.progress) progress.resetProgress();
        if (state.towers) progress.resetTowers();
        if (state.score) new LocalScoreService(saveStorage()).clear();
        // Bags got their own row on 2026-08-25 (player request); before that
        // they were cleared only by an everything-selected wipe. Resetting
        // TOWERS alone still deliberately leaves them — a bag is paid-for
        // stock, not tower access.
        if (state.bags) progress.resetBags();
      } catch (err) {
        console.warn('[LevelSelectScene] Reset failed', err);
      }
      teardown();
      this.scene.restart();
    });

    // ── Cancel ───────────────────────────────────────────────────────────────
    const cancelBg = this.add
      .rectangle(panelCx + 120, btnY, 200, 52, 0x2a2a3a)
      .setStrokeStyle(2, 0x6666aa)
      .setInteractive({ useHandCursor: true })
      .setDepth(D + 2);
    objects.push(cancelBg);
    objects.push(
      this.add
        .text(panelCx + 120, btnY, 'CANCEL', {
          fontSize: '20px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 3),
    );
    cancelBg.on('pointerup', teardown);

    refresh(); // initial paint (all off, confirm disabled)
  }

  /**
   * One themed, interactive campaign panel, added to `container` (so a whole
   * chapter's rows can be torn down together on a tab switch). `accent` is the
   * chapter's colour (gold for Ch1, blue for Ch2); boss rows stay crimson.
   */
  private makeLevelRow(
    cx: number,
    rowY: number,
    level: LevelDef,
    accent: { num: number; hex: string },
    container: Phaser.GameObjects.Container,
  ): void {
    const rowH = ROW_H;
    const left = cx - ROW_W / 2;
    const top = rowY - rowH / 2;
    const right = cx + ROW_W / 2;
    const boss = hasBoss(level);
    const unlocked = this.progress.isUnlocked(level.id);
    const stars = this.progress.stars(level.id);
    const accentNum = !unlocked ? 0x555a66 : boss ? 0xc0392b : accent.num;
    const accentHex = !unlocked ? '#7a818c' : boss ? '#e8674f' : accent.hex;

    // Panel + frame (redrawn on hover). Locked rows sit dimmer and don't react.
    const panel = this.add.graphics();
    container.add(panel);
    const drawPanel = (hover: boolean) => {
      panel.clear();
      panel.fillStyle(0x15131f, unlocked ? (hover ? 0.95 : 0.8) : 0.6);
      panel.fillRoundedRect(left, top, ROW_W, rowH, 12);
      const lit = hover && unlocked;
      panel.lineStyle(lit ? 2 : 1, accentNum, lit ? 1 : 0.5);
      panel.strokeRoundedRect(left, top, ROW_W, rowH, 12);
      // Left accent bar.
      panel.fillStyle(accentNum, 1);
      panel.fillRoundedRect(left, top, 6, rowH, { tl: 12, bl: 12, tr: 0, br: 0 });
    };
    drawPanel(false);

    // Themed locale wash — the level's background art, masked to the panel.
    const bgKey =
      level.background && this.textures.exists(level.background) ? level.background : 'bg-forest';
    if (this.textures.exists(bgKey)) {
      const wash = this.add
        .image(cx, rowY, bgKey)
        .setDepth(0.5)
        .setAlpha(unlocked ? 0.38 : 0.15);
      const s = Math.max(ROW_W / wash.width, rowH / wash.height); // cover
      wash.setScale(s);
      const maskG = this.make.graphics();
      maskG.fillStyle(0xffffff);
      maskG.fillRoundedRect(left, top, ROW_W, rowH, 12);
      wash.setMask(maskG.createGeometryMask());
      // Mask graphics isn't a display child — tie its lifetime to the wash.
      wash.once('destroy', () => maskG.destroy());
      container.add(wash);
    }

    // Episode badge.
    const textX = left + 30;
    container.add(
      this.add
        .text(textX, rowY - 26, `EPISODE ${level.episode ?? level.id}`, {
          fontSize: '13px',
          color: accentHex,
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5)
        .setDepth(1),
    );

    // Parody title.
    container.add(
      this.add
        .text(textX, rowY + 2, level.name, {
          fontSize: '27px',
          color: unlocked ? '#ffffff' : '#7a818c',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5)
        .setDepth(1),
    );

    // Location subtitle — or, when locked, how to unlock it. Any win opens the
    // next episode now (REQUIRED_STARS_TO_ADVANCE === 1★ === "won"), so the copy
    // asks for a clear rather than a star count.
    const subtitle = unlocked ? level.subtitle : 'Beat the previous episode to unlock';
    if (subtitle) {
      container.add(
        this.add
          .text(textX, rowY + 28, subtitle, {
            fontSize: '14px',
            color: unlocked ? '#9aa6ad' : '#c08a3e',
            fontStyle: 'italic',
          })
          .setOrigin(0, 0.5)
          .setDepth(1),
      );
    }

    // Boss tag.
    if (boss) {
      container.add(
        this.add
          .text(right - 24, rowY - 26, '★ BOSS', {
            fontSize: '14px',
            color: unlocked ? '#e8674f' : '#7a818c',
            fontStyle: 'bold',
          })
          .setOrigin(1, 0.5)
          .setDepth(1),
      );
    }

    // Earned star rating (out of 3) — gold when unlocked, muted when locked.
    container.add(
      this.add
        .text(right - 24, boss ? rowY : rowY - 24, starString(stars), {
          fontSize: '18px',
          color: unlocked ? GOLD_HEX : '#555a66',
        })
        .setOrigin(1, 0.5)
        .setDepth(1),
    );

    // Best score.
    const scoreText = this.add
      .text(right - 24, rowY + 22, 'BEST: —', { fontSize: '16px', color: '#9aa6ad' })
      .setOrigin(1, 0.5)
      .setDepth(1);
    container.add(scoreText);
    void this.scoreService
      .best(level.id)
      .then((best) => scoreText.setText(best > 0 ? `BEST: ${best}` : 'BEST: —'))
      .catch(() => {
        /* non-critical — leave placeholder */
      });

    // Coin reward for clearing this level — shown on every card.
    // Coins then "+N EXP" beside them, all left of the LOCKED marker (right-150).
    // right-380 clears the longest title ("Return of the Golden Nugget", 27px
    // bold from left+30) — at right-420 the coin sat on its last letters.
    const rewardX = right - 380;
    const coinIcon = this.add.image(rewardX, rowY, 'coin').setDisplaySize(28, 28).setDepth(1);
    if (!unlocked) coinIcon.setAlpha(0.5);
    container.add(coinIcon);
    container.add(
      this.add
        .text(rewardX + 18, rowY, `+${level.coinReward}`, {
          fontSize: '18px',
          color: unlocked ? GOLD_HEX : '#7a818c',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5)
        .setDepth(1),
    );
    // Rank exp for the first win, right beside the coins (Chapters 2 and 3).
    const exp = expRewardFor(level);
    if (exp > 0) {
      container.add(
        this.add
          .text(rewardX + 96, rowY, `+${exp} EXP`, {
            fontSize: '15px',
            color: unlocked ? '#9fd8ff' : '#7a818c',
            fontStyle: 'bold',
          })
          .setOrigin(0, 0.5)
          .setDepth(1),
      );
    }

    // "Locked" marker for locked levels. Plain text, not the 🔒 emoji: Phaser's
    // canvas text renders the padlock's UTF-16 surrogate pair as two tofu boxes.
    if (!unlocked) {
      container.add(
        this.add
          .text(right - 150, rowY, 'LOCKED', {
            fontSize: '20px',
            color: '#6a7078',
            fontStyle: 'bold',
          })
          .setOrigin(0.5)
          .setDepth(1),
      );
    }

    // Interactive hit zone — only for unlocked levels. (Level 1 is always
    // unlocked, so the e2e click target at 640,175 stays valid.)
    const hit = this.add.zone(cx, rowY, ROW_W, rowH).setOrigin(0.5);
    container.add(hit);
    if (unlocked) {
      hit.setInteractive({ useHandCursor: true });
      hit.on('pointerover', () => drawPanel(true));
      hit.on('pointerout', () => drawPanel(false));
      hit.on('pointerup', (p: Phaser.Input.Pointer) => {
        if (p.getDistance() >= SWIPE_MIN) return; // a swipe-scroll, not a tap
        this.scene.start('GameScene', { levelId: level.id });
        this.scene.launch('HudScene', { levelId: level.id });
      });
    }
  }
}
